// WHICH PRODUCT DOES A CUSTOMER'S PO LINE NAME?
//
// A pure module, like poWarnings and inventoryRows, so the rules can be
// tested on data rather than inferred from a screen. It decides ONE thing per
// PO line: does this part number name a product on our master, exactly?
//
// ⚠️ EXACT ONLY. THERE IS NO FUZZY MATCHING HERE AND THERE MUST NEVER BE.
// A wrong match is not a cosmetic error: it puts a real product id, brand and
// discount rule onto a line the customer ordered something else for, and
// every number downstream then looks properly sourced. "No match" is a safe
// answer that a person fixes in seconds; a confident wrong match is one
// nobody looks for.
//
// ⚠️ WHAT "NORMALISED" IS ALLOWED TO MEAN, AND WHAT IT IS NOT.
// Only differences that CANNOT change which part is meant:
//
//   * case             -- `nrs-pd-48` and `NRS-PD-48` are one part number;
//   * surrounding and internal whitespace -- a PDF's column can break a part
//     number across a space, and `NRS PD 48` printed in one cell is the same
//     string typed without the gaps.
//
// ⚠️ PUNCTUATION IS NOT STRIPPED, DELIBERATELY. Removing hyphens, dots or
// slashes would make `NRS-PD-48-800` equal to `NRSPD48800` -- and in a
// catalogue of structured part numbers, two different parts really can differ
// by exactly that. That is fuzzy matching wearing a normaliser's clothes. The
// consequence to know: a PO that prints the part number WITHOUT its hyphens
// will not match, and the line arrives unmatched for a person to fix. That is
// the safe direction and it was a deliberate choice; widening it is an owner
// decision, not a tidy-up.
//
// ⚠️ THE SUPPLIER'S PART NUMBER IS NEVER MATCHED ON. A customer PO carries the
// VENDOR's own part number for the same goods (Oracle's POs print the supplier's),
// and it is a different numbering system that our master knows nothing about.
// Matching on it would join two catalogues by coincidence. It is carried on
// the PO line for display and comparison and stops there --
// `matchPoLines` never reads the field, and its suite asserts that a PO line
// whose supplier number IS a real product part number still comes back
// unmatched.

import type { CatalogProduct } from "@/types";

/** The only part of a saved PO line this module reads.
 *
 *  ⚠️ STRUCTURAL AND DELIBERATELY NARROW -- it names `part_number` and
 *  `quantity` and nothing else. api.ts's `PoLine` carries
 *  `anixter_part_number` (the supplier's own code), `description`, `manufacturer` and more; typing
 *  against the whole row would let a later edit reach for one of them here,
 *  and the supplier number is the one field that must never be matched on.
 *  A `PoLine` satisfies this type, so callers pass one straight in. */
export interface PoLineLike {
  part_number?: string | null;
  quantity?: number | null;
}

/** A part number reduced to what cannot change its identity. "" when there is
 *  nothing to compare -- and "" NEVER matches, including another "". */
export function normalisePartNumber(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, "").toUpperCase();
}

/** Why a line has no product. Each one is shown to the person as-is. */
export type NoMatchReason =
  /** The PO printed no part number at all (an unreadable cell, or a line of
   *  pure description). */
  | "no_part_number"
  /** A real part number that our master does not carry. */
  | "not_in_catalogue"
  /** ⚠️ FOUND, BUT UNUSABLE. The product exists and its brand or category is
   *  a name this build's closed unions have never seen, so the add path would
   *  DROP it silently (`if (!product.brand || !product.category) continue`).
   *  Reporting it as unmatched is the honest answer: a person sees the line
   *  and can fix the product record, rather than watching it vanish. */
  | "brand_not_recognised";

export interface PoLineMatch {
  /** The PO line this decision is about, unchanged. */
  line: PoLineLike;
  /** The product it names, or null. */
  product: CatalogProduct | null;
  reason?: NoMatchReason;
}

export const NO_MATCH_TEXT: Record<NoMatchReason, string> = {
  no_part_number: "No part number printed — add this line by hand",
  not_in_catalogue: "Not in the product master — will be added as a free-text line",
  brand_not_recognised:
    "Found, but its brand or category is not one this app knows — will be added as a free-text line",
};

/** Match each PO line against candidate products, exactly.
 *
 *  `candidates` is whatever the caller has loaded from the product master --
 *  the caller searches, this decides. Passing a wider list than necessary is
 *  safe: nothing here matches on anything but an exact normalised part
 *  number, so an extra candidate cannot become a match.
 *
 *  ⚠️ AN AMBIGUOUS PART NUMBER IS NOT A MATCH. Part numbers are NOT unique on
 *  the real master and the backend deliberately has no constraint saying they
 *  are (see CLAUDE.md). If two products share one normalised part number,
 *  this cannot know which the customer meant, so it answers
 *  `not_in_catalogue` rather than taking the first. Choosing for the person
 *  here would put the wrong product id on the line silently. */
export function matchPoLines(
  lines: PoLineLike[] | null | undefined,
  candidates: CatalogProduct[] | null | undefined,
): PoLineMatch[] {
  // ⚠️ DE-DUPLICATED BY PRODUCT ID, AND THIS IS LOAD-BEARING RATHER THAN
  // TIDY. A caller building `candidates` from one search PER PART NUMBER
  // accumulates overlapping result sets -- the endpoint does a substring
  // match, so searching two part numbers can return the same product twice.
  // Without this, that duplicate reads as "two products share this part
  // number", the ambiguity rule below fires, and a perfectly good match is
  // refused. Caught by a test whose two searches both returned the one
  // product in the fixture.
  //
  // The ambiguity rule is about two DIFFERENT products sharing a part number,
  // which is a real thing on the master. The same product listed twice is
  // not that.
  const byPart = new Map<string, Map<number, CatalogProduct>>();
  for (const product of candidates ?? []) {
    const key = normalisePartNumber(product?.partNo);
    if (!key) continue;
    const bucket = byPart.get(key) ?? new Map<number, CatalogProduct>();
    bucket.set(product.id, product);
    byPart.set(key, bucket);
  }

  return (lines ?? []).map((line) => {
    // ⚠️ `part_number` ONLY. Never `anixter_part_number`, never
    // `description`, never `manufacturer`.
    const key = normalisePartNumber(line?.part_number);
    if (!key) return { line, product: null, reason: "no_part_number" as const };

    const found = byPart.get(key);
    if (!found || found.size !== 1) {
      return { line, product: null, reason: "not_in_catalogue" as const };
    }

    const product = [...found.values()][0];
    if (!product.brand || !product.category) {
      return { line, product: null, reason: "brand_not_recognised" as const };
    }
    return { line, product };
  });
}

/** The distinct normalised part numbers worth searching the master for.
 *
 *  Used to turn a PO into the fewest catalogue reads: one per distinct part
 *  number, and none at all for lines that printed nothing. */
export function partNumbersToLookUp(
  lines: PoLineLike[] | null | undefined,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines ?? []) {
    const raw = (line?.part_number ?? "").trim();
    if (!raw) continue;
    const key = normalisePartNumber(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    // ⚠️ THE RAW TEXT IS WHAT IS SEARCHED, not the normalised key. The
    // endpoint matches against what is STORED, and the stored part number
    // carries its own spacing and case -- searching `NRSPD48` would miss a
    // master row reading `NRS PD 48`. The normalised form is only ever used
    // to compare, never to query.
    out.push(raw);
  }
  return out;
}

/** Quantity as it should reach a quote line.
 *
 *  ⚠️ EMPTY STAYS EMPTY, AND 0 IS NOT A DEFAULT TO BE HELPFUL ABOUT. A PO
 *  line whose quantity could not be read is saved as 0, and a quote line with
 *  qty 0 cannot price -- which the finalise guard already refuses, by name,
 *  with a message naming the line. Substituting 1 would invent an order
 *  quantity nobody printed, and it would price, and nothing would ever
 *  question it. This function exists to be the one place that says so. */
export function quantityForQuoteLine(quantity: number | null | undefined): number {
  return typeof quantity === "number" && Number.isFinite(quantity) && quantity > 0
    ? quantity
    : 0;
}
